import { cooKeys, getCookie, setCookie } from "goi_common";
import { useRouter } from "next/router";

const ONE_DAY_IN_SECONDS = 60 * 60 * 24;

/**
 * 네이버 브랜드 검색광고 캠페인 식별자.
 *
 * 실제 값은 `NBrandAD/` 로 슬래시가 붙어 들어오는데, 정확 일치로 받으면 마케팅이
 * 슬래시를 떼거나 `NBrandAD/brand1` 처럼 쪼갤 때 조용히 안 잡힌다. 이 건에서는
 * 누락(상담 시간대를 받아버림)이 과포함(상담 안 해도 될 사람을 relaxed 로 보냄)보다
 * 나쁘므로 `includes` 로 느슨하게 받는다.
 *
 * 배열인 이유는 대상 캠페인이 늘어날 수 있어서다. 지금은 하나뿐이고, 늘어나면
 * 여기만 고친다. (`useTrackCostComparisonEntry` 의 `PAID_AD_CAMPAIGN_KEYWORDS` 와 같은 모양)
 */
const BRAND_AD_CAMPAIGN_KEYWORDS = ["NBrandAD"];

/**
 * 브랜드 검색광고 진입자를 쿠키로 표시한다.
 *
 * ## 왜 필요한가
 *
 * 여유 고객 중 브랜드 검색광고 유입분은 상담을 당장 진행하지 않고 나중에 몰아서 한다.
 * 그 동안 결과 화면에서 상담 희망 시간을 받으면 **지키지 못할 약속**이 되고, 선택 즉시
 * `PATCH` 가 나가 운영 큐에 콜백 요청이 쌓인다. 그래서 이 집단은 상담 시간대 UI 가 없는
 * `/match/recommendation/relaxed` 로 보낸다. 이 쿠키가 그 판정의 입력값이다.
 *
 * ## 왜 랜딩 시점에 심어야 하는가
 *
 * 제출은 진입 한참 뒤에 일어나고, 그 사이 내부 이동으로 URL 에서 utm 이 사라진다.
 * 제출 시점에 `router.query.utm_campaign` 을 보면 거의 항상 비어 있다.
 *
 * ## 쿠키인 이유 (sessionStorage 아님)
 *
 * 실측상 브랜드광고 랜딩 → 제출까지 96% 가 1시간 이내지만, 2.8% 는 **탭을 닫았다가**
 * 24시간 안에 돌아와 제출한다. sessionStorage 는 탭 수명에 묶여 그 구간을 놓친다.
 * 유입 라벨은 개인정보가 아니고 세션을 넘어 유지돼야 하는 값이라 쿠키가 맞다.
 * 24시간을 넘긴 1.2% 는 의도적으로 포기한다 — "브랜드광고로 왔던 사람" 이 영원히
 * 보류 대상으로 남지 않게 하는 끊음이 필요하다.
 *
 * ## 이미 있으면 건드리지 않는다 (첫 터치 고정)
 *
 * early return 이 두 가지를 동시에 해결한다.
 * 1. **덮어쓰기 방지** — 브랜드광고로 들어온 뒤 다른 캠페인을 거쳐도 판정이 유지된다
 * 2. **TTL 연장 방지** — 만료 시계는 첫 진입에서 시작한다. 재방문해도 늘어나지 않는다
 */
export const useTrackBrandSearchAdEntry = () => {
  const router = useRouter();

  const trackEntry = () => {
    if (getCookie(cooKeys.brand_search_ad_entry)) return;

    const utmCampaign =
      typeof router.query.utm_campaign === "string" ? router.query.utm_campaign : "";

    const isBrandSearchAd = BRAND_AD_CAMPAIGN_KEYWORDS.some((keyword) =>
      utmCampaign.includes(keyword),
    );

    if (isBrandSearchAd) {
      setCookie(cooKeys.brand_search_ad_entry, "true", ONE_DAY_IN_SECONDS);
    }
  };

  return { trackEntry };
};

/**
 * 브랜드 검색광고로 진입한 적이 있는가. 결과 화면 분기에서 읽는다.
 *
 * 훅이 아니라 함수인 이유: 호출부가 렌더 중 한 번 읽고 끝내는 자리라
 * 구독이 필요 없다. 쿠키는 랜딩 시점에만 쓰이고 그 뒤로 바뀌지 않는다.
 */
export const isBrandSearchAdEntry = (): boolean =>
  Boolean(getCookie(cooKeys.brand_search_ad_entry));
